import { Link } from "react-router-dom";
import { Phone, Camera, ArrowRight, CheckCircle2, MessageSquare } from "lucide-react";
import Hero from "@/components/ui/Hero";
import TrustStrip from "@/components/ui/TrustStrip";
import ServiceCard from "@/components/ui/ServiceCard";
import LocationCard from "@/components/ui/LocationCard";
import CTABlock from "@/components/ui/CTABlock";
import FAQAccordion from "@/components/ui/FAQAccordion";
import CompletedWorkGrid from "@/components/ui/CompletedWorkGrid";
import CompletedWorkPhoto from "@/components/ui/CompletedWorkPhoto";
import OwnerPhoto from "@/components/ui/OwnerPhoto";
import Reveal from "@/components/ui/Reveal";
import Since1999Stamp from "@/components/ui/Since1999Stamp";
import SwflMap from "@/components/ui/SwflMap";
import RepairOrRescreenCard from "@/components/ui/RepairOrRescreenCard";
import {
    TornMesh,
    MeshRoll,
    StormCage,
    CageFrameCorner,
    CondoPanel,
} from "@/components/ui/icons/A1Icons";
import HomeReviewsSection from "@/components/sections/HomeReviewsSection";
import { COMPLETED_WORK_PHOTOS } from "@/data/galleryPhotos";
import { SERVICES } from "@/data/services";
import { LOCATIONS } from "@/data/locations";
import { BUSINESS, USP_PILLARS, PHASE3_BRAND_VOICE } from "@/lib/constants";
import { track, EVENTS } from "@/lib/analytics";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function Home() {
    useDocumentTitle(
        "Pool Cage Repair & Rescreening in Naples, FL · A1 Screening",
        "Naples-based pool cage repair, rescreening, lanai, and screen door repair across Southwest Florida. Family-owned since 1999. Free estimates. Call 239-434-7306."
    );
    const naples = LOCATIONS.find((l) => l.is_primary);
    const secondary = LOCATIONS.filter((l) => !l.is_primary);

    const homepageFAQs = [
        {
            q: "Who fixes pool screens near me?",
            a: "A1 Screening repairs pool screens across Naples and Southwest Florida. Family-owned since 1999. Call 239-434-7306 or request a free estimate online.",
        },
        {
            q: "How much does pool cage rescreening cost in Naples?",
            a: "Pool cage rescreening costs in Naples depend on the size of the enclosure, number of panels, screen material, access, storm damage, and whether structural repairs are needed. We provide free estimates so homeowners can get accurate pricing.",
        },
        {
            q: "Do I need full rescreening or just a repair?",
            a: "If you have one or two torn panels, a spot repair is usually enough. If your screens are sun-faded, brittle, or torn in multiple panels, a full rescreen is usually the better long-term choice.",
        },
        {
            q: "Can storm-damaged pool screens be repaired?",
            a: "Yes — most storm-damaged pool cages and screens can be repaired or rescreened. Storm repair availability may vary by schedule and season.",
        },
    ];

    return (
        <>
            <Hero
                eyebrow="Family-Owned · Serving Southwest Florida Since 1999"
                title={
                    <>
                        Pool Cage Repair &amp; Rescreening in <span className="a1-accent">Naples, FL</span>
                    </>
                }
                subtitle="Family-owned, serving Southwest Florida since 1999. Free estimates. Clean work. Naples-based — not a call center two states away."
                primaryCta={{ to: "/free-estimate", label: "Get a Free Estimate" }}
                secondaryCta={{ to: "/upload-photos", label: "Upload Photos for Estimate Review", icon: Camera }}
                tertiary={{ to: `/service-areas/${naples.slug}`, label: "See Naples service" }}
                media={{ photo: COMPLETED_WORK_PHOTOS[1], badge: "Since 1999" }}
            />
            <TrustStrip />

            {/* Services overview */}
            <section className="a1-section" data-testid="services-overview">
                <div className="a1-container">
                    <div className="max-w-3xl mb-10 md:mb-14">
                        <p className="a1-eyebrow mb-3">What We Do</p>
                        <h2 className="mb-4 a1-h2-underline">Screen Enclosure Services</h2>
                        <p
                            className="text-lg leading-relaxed"
                            style={{ color: "var(--color-text-muted)" }}
                        >
                            Every job is handled by our own crew — no subcontractors, no surprises. From a single torn panel to a full pool cage rescreen, we handle the screen and structural work most Naples homes need.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {SERVICES.map((s) => (
                            <ServiceCard key={s.slug} service={s} />
                        ))}
                    </div>
                    <div className="mt-10">
                        <div className="a1-card a1-card-feature flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 justify-between">
                            <p className="text-base leading-relaxed flex-1" style={{ color: "var(--color-text)" }}>
                                Not sure what you need? <strong className="a1-accent">Send us a photo</strong> — we'll tell you repair vs. rescreen.
                            </p>
                            <a
                                href={`tel:${BUSINESS.phoneTel}`}
                                onClick={() => track(EVENTS.PHONE_CLICK, { placement: "services-overview-cta" })}
                                className="a1-btn-primary whitespace-nowrap"
                                data-testid="services-cta-call"
                            >
                                <Phone size={16} strokeWidth={2.5} /> Call for Free Estimate
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Owner / Why A-1 — mirrors the John section on the original site */}
            <section className="a1-bg-alt a1-section" data-testid="owner-section">
                <div className="a1-container grid lg:grid-cols-12 gap-10 items-center">
                    {/* Owner photo — John Stephens, Owner of A1 Screening */}
                    <div className="lg:col-span-5">
                        <OwnerPhoto variant="framed-card" testId="owner-photo-home" />
                    </div>
                    <div className="lg:col-span-7">
                        <p className="a1-eyebrow mb-3">Local Trust</p>
                        <h2 className="mb-3 a1-h2-underline">
                            Family-Owned Screen Repair in Naples, FL
                        </h2>
                        <p
                            className="uppercase tracking-[0.18em] text-[12px] font-bold mb-5"
                            style={{ color: "var(--color-accent)" }}
                        >
                            Family-Owned & Serving Southwest Florida Since 1999
                        </p>
                        <p className="text-lg leading-relaxed mb-4" style={{ color: "var(--color-text-muted)" }}>
                            A1 Screening is led by owner John Stephens and serves homeowners, condos, HOAs, and property managers across Southwest Florida. From pool cages to condos, we screen it all.
                        </p>
                        <p className="leading-relaxed mb-4" style={{ color: "var(--color-text-muted)" }}>
                            We've helped homeowners across Naples, Fort Myers, Cape Coral, Lehigh Acres, and Bonita Springs with torn screens, storm damage, and pool-cage repairs. Final recommendations and pricing are confirmed after technician review.
                        </p>
                        <p className="leading-relaxed mb-6 font-semibold" style={{ color: "var(--color-text)" }}>
                            No runaround. No missed appointments. Just reliable service.
                        </p>
                        <div className="grid grid-cols-2 gap-3 mb-7">
                            {[
                                "Free written estimates",
                                "Honest pricing",
                                "Reliable service",
                                "Local experience",
                            ].map((label) => (
                                <span key={label} className="a1-check-pill text-[14px]">
                                    <Check /> {label}
                                </span>
                            ))}
                        </div>
                        <a
                            href={`tel:${BUSINESS.phoneTel}`}
                            onClick={() => track(EVENTS.PHONE_CLICK, { placement: "owner-section" })}
                            className="a1-btn-primary"
                            data-testid="owner-section-cta"
                        >
                            <Phone size={18} strokeWidth={2.5} /> Call Now for a Free Estimate
                        </a>
                        <Since1999Stamp />
                    </div>
                </div>
            </section>

            <div className="a1-mesh-divider" aria-hidden="true" />

            {/* A1 Screening project photos — real job photos */}
            <section className="a1-section" data-testid="completed-work-section">
                <div className="a1-container">
                    <Reveal>
                        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
                            <div className="max-w-2xl">
                                <p className="a1-eyebrow mb-3">Project Photos</p>
                                <h2 className="mb-4 a1-h2-underline">A1 Screening Project Photos</h2>
                                <p className="text-lg leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                                    Real pool cage and screen enclosure project photos from A1 Screening. Photos are shown as project examples — not as before-and-after comparisons. Final recommendations and pricing are confirmed after technician review.
                                </p>
                            </div>
                            <Link to="/gallery" className="a1-btn-ghost self-start lg:self-auto" data-testid="completed-work-see-more">
                                See more project photos <ArrowRight size={16} />
                            </Link>
                        </div>
                    </Reveal>
                    <CompletedWorkGrid surface="home" limit={3} />
                    <Reveal delay={120}>
                        <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:justify-center">
                            <Link to="/free-estimate" className="a1-btn-primary" data-testid="completed-work-cta-quote">
                                Request a Free Estimate <ArrowRight size={18} />
                            </Link>
                            <a
                                href={`tel:${BUSINESS.phoneTel}`}
                                onClick={() => track(EVENTS.PHONE_CLICK, { placement: "completed-work-section" })}
                                className="a1-btn-secondary"
                                data-testid="completed-work-cta-call"
                            >
                                <Phone size={18} strokeWidth={2.5} /> Call {BUSINESS.phone}
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Naples-first */}
            <section className="a1-section" data-testid="naples-first">
                <div className="a1-container grid lg:grid-cols-12 gap-10 items-start">
                    <Reveal className="lg:col-span-7">
                        <p className="a1-eyebrow mb-3">Home Base</p>
                        <h2 className="mb-4 a1-h2-underline">Naples Is Our Home Base — Not an Afterthought</h2>
                        <p className="text-lg leading-relaxed mb-4" style={{ color: "var(--color-text-muted)" }}>
                            {PHASE3_BRAND_VOICE.positioning} From Pelican Bay to Olde Naples to Lely, we know the neighborhoods, the cages, and the climate. When you call, you're talking to a Naples-based team — not a call center two states away.
                        </p>
                        <p
                            className="text-[12px] font-bold uppercase tracking-[0.18em] mb-3"
                            style={{ color: "var(--color-accent)" }}
                        >
                            Neighborhoods we work in
                        </p>
                        <div className="flex flex-wrap gap-2 mb-7">
                            {(naples.neighborhoods || []).slice(0, 6).map((n) => (
                                <span key={n} className="a1-chip">{n}</span>
                            ))}
                        </div>
                        <Link to={`/service-areas/${naples.slug}`} className="a1-btn-primary">
                            See Naples service <ArrowRight size={16} />
                        </Link>
                    </Reveal>
                    <Reveal className="lg:col-span-5" delay={120}>
                        <CompletedWorkPhoto photo={COMPLETED_WORK_PHOTOS[1]} />
                    </Reveal>
                </div>
            </section>

            {/* Photo / Estimate review — 3-step panel */}
            <section className="a1-bg-alt a1-section" data-testid="photo-teaser">
                <div className="a1-container">
                    <Reveal>
                        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
                            <p className="a1-eyebrow mb-3 justify-center" style={{ display: "inline-flex" }}>Estimate Review</p>
                            <h2 className="mb-4 a1-h2-underline">Send Photos. We'll Take It From There.</h2>
                            <p className="text-lg leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                                Photos help A1 Screening understand the project before follow-up. Final recommendations and pricing are confirmed after technician review.
                            </p>
                        </div>
                    </Reveal>
                    <div className="a1-steps-grid">
                        {[
                            {
                                step: "01",
                                icon: TornMesh,
                                title: "Snap a few photos",
                                body: "Wide shots of the cage plus close-ups of any tears, sagging, or bent panels. Sun angle doesn't matter.",
                            },
                            {
                                step: "02",
                                icon: CondoPanel,
                                title: "Upload in under a minute",
                                body: "No app to download, no login. Add your name, address, and what you've noticed — that's it.",
                            },
                            {
                                step: "03",
                                icon: CageFrameCorner,
                                title: "Technician review and follow-up",
                                body: "A1 Screening reviews the photos and follows up by your preferred channel — phone, text, or email — during business hours.",
                            },
                        ].map((s, idx) => {
                            const Icon = s.icon;
                            return (
                                <Reveal key={s.step} delay={idx * 90}>
                                    <div className="a1-step-card">
                                        <span className="a1-step-num" aria-hidden="true">{s.step}</span>
                                        <span className="a1-step-icon" aria-hidden="true"><Icon size={20} strokeWidth={1.8} /></span>
                                        <h3 className="text-lg mb-2 leading-tight">{s.title}</h3>
                                        <p className="text-[15px] leading-relaxed" style={{ color: "var(--color-text-muted)" }}>{s.body}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                    <Reveal delay={280}>
                        <div className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-3 justify-center">
                            <Link to="/upload-photos" className="a1-btn-primary" data-testid="photo-teaser-cta">
                                <Camera size={18} /> Upload Photos
                            </Link>
                            <a
                                href={`tel:${BUSINESS.phoneTel}`}
                                onClick={() => track(EVENTS.PHONE_CLICK, { placement: "photo-teaser" })}
                                className="a1-btn-secondary"
                            >
                                <Phone size={18} strokeWidth={2.5} /> Or call us
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Common Problems */}
            <section className="a1-section" data-testid="problems-section">
                <div className="a1-container">
                    <div className="max-w-3xl mb-10">
                        <p className="a1-eyebrow mb-3">Common Problems</p>
                        <h2 className="mb-3 a1-h2-underline">Torn Screens? Bugs Getting Through? Storm Damage?</h2>
                        <p className="text-lg leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                            Most pool cages and lanais we see have one of three problems. Here's how we fix each.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        {[
                            { icon: TornMesh, title: "Torn screens or holes", body: "One small tear becomes a full panel fast. We patch it right or rescreen the whole cage. Your call." },
                            { icon: MeshRoll, title: "Bugs and no-see-ums", body: "If no-see-ums are getting through, your screen is finished. We use the right mesh for Southwest Florida — not generic mesh." },
                            { icon: StormCage, title: "Storm damage", body: `After the storm, your pool cage shouldn't wait. We'll come look, take photos, and give you a free estimate. ${PHASE3_BRAND_VOICE.cautious_storm}` },
                        ].map((p, idx) => {
                            const Icon = p.icon;
                            return (
                                <Reveal key={p.title} delay={idx * 90}>
                                    <div className="a1-card a1-card-feature a1-problem-card flex flex-col gap-3 h-full">
                                        <span className="a1-problem-num" aria-hidden="true">{String(idx + 1).padStart(2, "0")}</span>
                                        <div className="a1-icon-wrap-lg a1-problem-icon">
                                            <Icon size={26} strokeWidth={1.8} />
                                        </div>
                                        <h3 className="text-xl">{p.title}</h3>
                                        <p className="leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                                            {p.body}
                                        </p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <RepairOrRescreenCard />

            {/* Get In Touch — fast response */}
            <section className="a1-bg-alt a1-section" data-testid="get-in-touch">
                <div className="a1-container max-w-3xl text-center">
                    <p className="a1-eyebrow mb-3 justify-center" style={{ display: "inline-flex" }}>Get In Touch</p>
                    <h2 className="mb-5 a1-h2-underline">Fast Response When You Call or Text</h2>
                    <p className="text-lg leading-relaxed mb-3" style={{ color: "var(--color-text-muted)" }}>
                        If we miss your call, you'll get a quick text back so we can help you right away.
                    </p>
                    <p className="leading-relaxed mb-8" style={{ color: "var(--color-text-muted)" }}>
                        We make it easy to get a quote without waiting around or chasing contractors.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
                        <a
                            href={`tel:${BUSINESS.phoneTel}`}
                            onClick={() => track(EVENTS.PHONE_CLICK, { placement: "get-in-touch" })}
                            className="a1-btn-primary"
                            data-testid="get-in-touch-call"
                        >
                            <Phone size={18} strokeWidth={2.5} /> Call {BUSINESS.phone}
                        </a>
                        <Link to="/free-estimate" className="a1-btn-secondary" data-testid="get-in-touch-quote">
                            <MessageSquare size={18} /> Request a Quote
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <span className="a1-check-pill text-[13px] justify-center"><Check /> No answering machines</span>
                        <span className="a1-check-pill text-[13px] justify-center"><Check /> Quick text back during business hours</span>
                        <span className="a1-check-pill text-[13px] justify-center"><Check /> No pressure, no obligation</span>
                    </div>
                </div>
            </section>

            {/* Service areas */}
            <section className="a1-section" data-testid="service-areas-section">
                <div className="a1-container">
                    <div className="max-w-3xl mb-10">
                        <p className="a1-eyebrow mb-3">Service Area</p>
                        <h2 className="mb-3 a1-h2-underline">Pool Cage &amp; Lanai Screen Repair Across Southwest Florida</h2>
                        <p className="text-lg leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                            Naples is our home base. We also serve {BUSINESS.secondaryCities.join(", ")} — Southwest Florida homeowners we've been working with for years. Local crew, no subcontractors, no surprise fees.
                        </p>
                    </div>
                    <div className="grid lg:grid-cols-12 gap-8 items-start">
                        <Reveal className="lg:col-span-5">
                            <SwflMap />
                        </Reveal>
                        <div className="lg:col-span-7 flex flex-col gap-5">
                            <LocationCard location={naples} featured />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                {secondary.map((l) => (
                                    <LocationCard key={l.slug} location={l} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why choose us */}
            <section className="a1-bg-alt a1-section">
                <div className="a1-container">
                    <div className="max-w-3xl mb-10">
                        <p className="a1-eyebrow mb-3">Why Choose A-1</p>
                        <h2 className="mb-3 a1-h2-underline">Why Naples Homeowners Choose A1 Screening</h2>
                        <p className="text-lg leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                            Six reasons we get hired again and again across Southwest Florida.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {USP_PILLARS.map((p, i) => (
                            <div key={p.title} className="a1-card flex flex-col gap-3">
                                <div className="flex items-center gap-3">
                                    <div className="a1-step-num !w-10 !h-10 !text-[16px]">{i + 1}</div>
                                    <h3 className="text-lg leading-tight">{p.title}</h3>
                                </div>
                                <p className="leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                                    {p.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ preview */}
            <section className="a1-section">
                <div className="a1-container max-w-3xl">
                    <div className="text-center mb-10">
                        <p className="a1-eyebrow mb-3 justify-center" style={{ display: "inline-flex" }}>Common Questions</p>
                        <h2 className="mb-3 a1-h2-underline">Screen Enclosure Questions — Answered</h2>
                        <p className="leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                            Real questions Naples homeowners ask us. <Link to="/faq">See the full FAQ</Link>.
                        </p>
                    </div>
                    <FAQAccordion items={homepageFAQs} prefix="home-faq" />
                    <div className="mt-8 text-center flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/faq" className="a1-btn-secondary">All FAQs <ArrowRight size={16} /></Link>
                        <Link to="/free-estimate" className="a1-btn-primary">
                            <CheckCircle2 size={18} /> Get a Free Estimate
                        </Link>
                    </div>
                </div>
            </section>

            <HomeReviewsSection />

            <div className="a1-mesh-divider" aria-hidden="true" />

            <CTABlock />
        </>
    );
}

function Check() {
    return (
        <span
            aria-hidden="true"
            className="inline-flex items-center justify-center"
            style={{
                width: 18,
                height: 18,
                borderRadius: 999,
                border: "1.5px solid var(--color-accent)",
                color: "var(--color-accent)",
                flexShrink: 0,
            }}
        >
            <svg viewBox="0 0 12 12" width="10" height="10" fill="none">
                <path d="M2.5 6.5l2.5 2.5L9.5 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </span>
    );
}
